/**
 * Normalizes entity bodies before PUT/POST to the Parts / Assemblies API.
 * New non-nullable booleans and similar fields should get defaults here so callers
 * do not need to remember them on every screen.
 *
 * System.Text.Json treats omitted JSON properties as default(false) for bools, which
 * would clear flags like storefront listing when EF attaches full entities.
 *
 * Part MSRP margin/rounding columns use the same defaults as the API migration so
 * lightweight create payloads (e.g. Parts.js) stay consistent.
 */

function withCustomerListingDefaults<T extends Record<string, unknown>>(entity: T): T {
    return {
        ...entity,
        isCustomerListed: (entity as { isCustomerListed?: boolean }).isCustomerListed ?? true
    } as T;
}

const DEFAULT_MSRP_ROUND_THRESHOLD = 15;
const DEFAULT_MSRP_ROUND_BELOW_INCREMENT = 0.25;
const DEFAULT_MSRP_ROUND_AT_OR_ABOVE_INCREMENT = 1;

/** When key absent / null / empty string, use fallback; otherwise coerce to finite number. */
function pickFiniteNumber(part: Record<string, unknown>, key: string, fallback: number): number {
    if (!(key in part) || part[key] === null || part[key] === undefined || part[key] === '') {
        return fallback;
    }
    const n = Number(part[key]);
    return Number.isFinite(n) ? n : fallback;
}

/** API stores FileURLs as a ~|~-delimited string; UI often keeps an array while editing. */
function normalizeFileUrlsForWrite(value: unknown): string {
    if (value == null) return '';
    if (typeof value === 'string') return value;
    if (Array.isArray(value)) {
        return value
            .filter((x) => x != null && String(x).trim() !== '')
            .map((x) => String(x).trim())
            .join('~|~');
    }
    return String(value);
}

/** Part body for POST /parts or PUT /parts */
export function normalizePartWritePayload(part: any): any {
    if (!part || typeof part !== 'object') return part;
    const base = withCustomerListingDefaults(part) as Record<string, unknown>;
    return {
        ...base,
        msrpUseMarginPricing: Boolean((base as { msrpUseMarginPricing?: boolean }).msrpUseMarginPricing),
        msrpMarginPercent: pickFiniteNumber(base, 'msrpMarginPercent', 0),
        msrpRoundThreshold: pickFiniteNumber(base, 'msrpRoundThreshold', DEFAULT_MSRP_ROUND_THRESHOLD),
        msrpRoundBelowIncrement: pickFiniteNumber(base, 'msrpRoundBelowIncrement', DEFAULT_MSRP_ROUND_BELOW_INCREMENT),
        msrpRoundAtOrAboveIncrement: pickFiniteNumber(
            base,
            'msrpRoundAtOrAboveIncrement',
            DEFAULT_MSRP_ROUND_AT_OR_ABOVE_INCREMENT
        ),
        msrpTiersJson:
            typeof (base as { msrpTiersJson?: string | null }).msrpTiersJson === 'string'
                ? (base as { msrpTiersJson: string }).msrpTiersJson
                : null,
    };
}

/** Assembly body for POST/PUT assemblies/assembly */
export function normalizeAssemblyWritePayload(assembly: any): any {
    if (!assembly || typeof assembly !== 'object') return assembly;
    return withCustomerListingDefaults({
        ...assembly,
        fileURLs: normalizeFileUrlsForWrite(assembly.fileURLs)
    });
}

/** SubAssembly body for POST/PUT assemblies/subAssembly */
export function normalizeSubAssemblyWritePayload(subAssembly: any): any {
    if (!subAssembly || typeof subAssembly !== 'object') return subAssembly;
    return withCustomerListingDefaults({
        ...subAssembly,
        fileURLs: normalizeFileUrlsForWrite(subAssembly.fileURLs)
    });
}

export function normalizePartWritePayloadArray(parts: any[] | null | undefined): any[] {
    if (!parts || !Array.isArray(parts)) return parts as any;
    return parts.map((p) => normalizePartWritePayload(p));
}

export function normalizeAssemblyWritePayloadArray(assemblies: any[] | null | undefined): any[] {
    if (!assemblies || !Array.isArray(assemblies)) return assemblies as any;
    return assemblies.map((a) => normalizeAssemblyWritePayload(a));
}

export function normalizeSubAssemblyWritePayloadArray(subAssemblies: any[] | null | undefined): any[] {
    if (!subAssemblies || !Array.isArray(subAssemblies)) return subAssemblies as any;
    return subAssemblies.map((s) => normalizeSubAssemblyWritePayload(s));
}
